import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../component/Header";
import Sidebar from "../../component/Sidebar";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

export default function StockCheckHistory() {
  const [sessions, setSessions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null); // { sessionId, results: [] }
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchSessions = async (date) => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/stockCheckSessions?date=${date}`);
      setSessions(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(selectedDate);
  }, [selectedDate]);

  const viewDetails = async (session) => {
    try {
      setLoadingDetails(true);
      const res = await axios.get(`${apiUrl}/stockCheckSessionDetails/${session.id}`);
      setDetails({ session, results: res.data });
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "matched": return "text-emerald-600 bg-emerald-50 border-emerald-100";
      case "missing": return "text-red-600 bg-red-50 border-red-100";
      case "unpaid": return "text-slate-600 bg-slate-50 border-slate-200";
      case "unpaid_found": return "text-indigo-600 bg-indigo-50 border-indigo-100";
      case "unregistered": return "text-amber-600 bg-amber-50 border-amber-100";
      default: return "text-slate-600 bg-slate-50 border-slate-100";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
      <Header />
      <Sidebar />
      <div className="flex-1 flex flex-col items-center py-10 px-4 md:ml-64 mt-16 transition-all duration-300">
        <div className="bg-white p-6 rounded-2xl shadow-xl border border-slate-100 w-full max-w-5xl">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
                <span className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">📜</span>
                Stock Check History
              </h1>
              <p className="text-slate-500 mt-1">Review past inventory checks by date</p>
            </div>
            
            <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl border border-slate-100">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-2">Filter Date:</span>
              <input 
                type="date" 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Session List */}
            <div className="lg:col-span-5 space-y-4">
              <h3 className="font-bold text-slate-700 flex items-center gap-2 mb-4">
                Checks on {new Date(selectedDate).toLocaleDateString()}
              </h3>
              
              {loading ? (
                <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <p className="text-slate-400 font-medium italic">No checks found for this date.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                  {sessions.map(session => (
                    <div 
                      key={session.id}
                      onClick={() => viewDetails(session)}
                      className={`p-4 rounded-xl border transition-all cursor-pointer group ${details?.session.id === session.id ? "bg-indigo-50 border-indigo-200 shadow-md ring-2 ring-indigo-500/20" : "bg-white border-slate-100 hover:border-indigo-200 hover:shadow-sm"}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-black text-slate-800">
                          {new Date(session.check_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">By {session.checked_by}</span>
                      </div>
                      <div className="grid grid-cols-5 gap-1">
                        <div className="text-center p-1 bg-emerald-50 rounded-lg border border-emerald-100">
                          <p className="text-[10px] font-black text-emerald-700">{session.total_matched}</p>
                          <p className="text-[7px] font-bold text-emerald-600 uppercase">Match</p>
                        </div>
                        <div className="text-center p-1 bg-red-50 rounded-lg border border-red-100">
                          <p className="text-[10px] font-black text-red-700">{session.total_missing}</p>
                          <p className="text-[7px] font-bold text-red-600 uppercase">Miss</p>
                        </div>
                        <div className="text-center p-1 bg-indigo-50 rounded-lg border border-indigo-100">
                          <p className="text-[10px] font-black text-indigo-700">{session.total_unpaid_found || 0}</p>
                          <p className="text-[7px] font-bold text-indigo-600 uppercase">U.In</p>
                        </div>
                        <div className="text-center p-1 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="text-[10px] font-black text-slate-700">{session.total_unpaid || 0}</p>
                          <p className="text-[7px] font-bold text-slate-600 uppercase">U.Out</p>
                        </div>
                        <div className="text-center p-1 bg-amber-50 rounded-lg border border-amber-100">
                          <p className="text-[10px] font-black text-amber-700">{session.total_unregistered}</p>
                          <p className="text-[7px] font-bold text-amber-600 uppercase">Unreg</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Session Details */}
            <div className="lg:col-span-7">
              {loadingDetails ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                   <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                   <p className="text-slate-500 font-bold animate-pulse">Loading session details...</p>
                </div>
              ) : details ? (
                <div className="bg-slate-50 rounded-3xl p-6 border border-slate-200 animate-fade-in h-full">
                   <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-black text-slate-800">Session Report</h3>
                      <button 
                        onClick={() => setDetails(null)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        ✕ Close
                      </button>
                   </div>

                   <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                      {details.results.map((item, idx) => (
                        <div key={idx} className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between group hover:shadow-md transition-all">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase border ${getStatusColor(item.status)}`}>
                                {item.status}
                              </span>
                              <span className="font-mono text-xs font-bold text-slate-800">
                                {item.order_number || item.barcode}
                              </span>
                            </div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              ID: {item.item_id || "N/A"} | Source: {item.table_name}
                            </div>
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                             <span className="text-xl">
                               {item.status === "matched" ? "✅" : item.status === "missing" ? "❌" : item.status === "unpaid_found" ? "💸" : item.status === "unpaid" ? "💳" : "⚠️"}
                             </span>
                          </div>
                        </div>
                      ))}
                   </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-slate-50 rounded-3xl border border-dashed border-slate-200 p-8 text-center">
                   <div className="text-5xl mb-4 grayscale opacity-30">🔍</div>
                   <h4 className="text-lg font-bold text-slate-500">No Session Selected</h4>
                   <p className="text-slate-400 text-sm mt-1">Select a session from the list on the left to view detailed results.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
